import { apiDeleteGlobalAnnouncements } from "@app/api/announcements/announcements.api";
import { DataPagination, DataTable } from "@app/components/atoms";
import { resetIsContinuousLoading, scrollToTop } from "@app/helpers";
import {
  AnnouncementGlobal,
  AnnouncementsTableProps,
} from "@app/types/announcements.types";
import { Button, Checkbox, Typography } from "@mui/material";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import { LIMIT_PAGE, Platform } from "@app/constants";
import { format } from "date-fns";

const AnnouncementsTable: React.FC<AnnouncementsTableProps> = ({
  timeFormat,
  announcements,
  announcementsLoading,
  field,
  setField,
  order,
  setOrder,
  getAnnouncements,
  pageInfo,
  setPageInfo,
}) => {
  const [isInit, setIsInit] = useState(true);

  const [, setOpen] = useState(false);

  const [, setDataCell] = useState<AnnouncementGlobal>();
  const { t } = useTranslation();
  const safeRows = announcements ?? [];
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);

  const handleChangePage = (page: number) => {
    setPageInfo({
      ...pageInfo,
      page,
    });
  };

  const handleCellClick = (column: any) => {
    if (!column.serialNumberPlatePictureUrl) {
      return;
    }
    setDataCell(column);
    setOpen(true);
  };

  const handleSort = (order: string | undefined, property: string) => {
    setOrder(order);
    setField(property);
  };

  const initScrollToTop = () => {
    if (isInit) {
      scrollToTop();
      setIsInit(false);
    }
  };

  async function handleDeleteAnnouncement(
    announcementIds: string[],
  ): Promise<void> {
    const params = {
      limit: LIMIT_PAGE,
      page: pageInfo.page,
    };
    const res = await apiDeleteGlobalAnnouncements(announcementIds);
    if (res.data.data) {
      setSelectedRowKeys((prevSelected) =>
        prevSelected.filter((id) => !announcementIds.includes(id)),
      );
    }
    getAnnouncements({
      ...(field && order
        ? { ...params, orderBys: [{ field, order }] }
        : { ...params }),
    });
  }

  const columns = [
    {
      key: "checkbox",
      title: (
        <>
          <Checkbox
            checked={
              safeRows.length > 0 && selectedRowKeys.length === safeRows.length
            }
            indeterminate={
              selectedRowKeys.length > 0 &&
              selectedRowKeys.length < safeRows.length
            }
            sx={{
              "&.Mui-checked": {
                color: "#140a9a",
              },
              "&.MuiCheckbox-indeterminate": {
                color: "#140a9a",
              },
            }}
            onChange={(e) => {
              const checked = e.target.checked;
              if (checked) {
                setSelectedRowKeys(
                  safeRows.map((row) => row.announcementId.toString()),
                );
              } else {
                setSelectedRowKeys([]);
              }
            }}
          />
        </>
      ),
      dataIndex: "id",
      width: 50,
      align: "center",
      forceRender: true,
      render: (_: any, record: AnnouncementGlobal) => (
        <Checkbox
          checked={selectedRowKeys.includes(record.announcementId.toString())}
          sx={{
            "&.Mui-checked": {
              color: "#140a9a",
            },
          }}
          onChange={(e) => {
            const checked = e.target.checked;
            const idStr = record.announcementId.toString();
            setSelectedRowKeys((prev) => {
              if (checked) {
                return [...prev, idStr];
              } else {
                return prev.filter((id) => id !== idStr);
              }
            });
          }}
        />
      ),
    },
    {
      key: "announcementType",
      title: "announcementType",
      dataIndex: "announcementType",
      sorted: true,
      align: "center",
      className: "pl-0 txt-pre-wrap",
      whiteSpace: "nowrap",
      style: {
        minWidth: 60,
      },
    },
    {
      key: "platform",
      title: "announcementTable.platform",
      dataIndex: "platform",
      sorted: true,
      whiteSpace: "nowrap",
      forceRender: true,
      render: (value: Platform | null, _record: AnnouncementGlobal) => {
        return <Typography>{value !== null ? value : "Both"}</Typography>;
      },
      style: {
        minWidth: 50,
      },
      className: "txt-pre-wrap",
    },
    {
      key: "createdTime",
      title: "announcementTable.createdTime",
      dataIndex: "createdTime",
      whiteSpace: "nowrap",
      sorted: true,
      style: {
        minWidth: 40,
      },
      render: (value: string) => {
        return (
          <span className="txt-nowrap">
            {format(new Date(value), timeFormat)}
          </span>
        );
      },
    },
    {
      key: "expiryTime",
      title: "announcementTable.expiryTime",
      dataIndex: "expiryTime",
      sorted: true,
      style: {
        minWidth: 170,
      },
      className: "txt-pre-wrap",
      render: (value: string) => {
        return (
          <span className="txt-nowrap">
            {format(new Date(value), timeFormat)}
          </span>
        );
      },
    },
    {
      key: "detail",
      title: "announcementTable.detail",
      dataIndex: "detail",
      style: {
        minWidth: 150,
      },
      className: "txt-pre-wrap",
    },
    {
      key: "delete",
      title: (
        <Button
          onClick={() => handleDeleteAnnouncement(selectedRowKeys)}
          className="btn-no-border-red"
          color={"error"}
          sx={{
            fontSize: "16px",
            lineHeight: "24px",
            letterSpacing: "0.5px",
            padding: "8px 12px",
            fontWeight: "700",
          }}
          style={{ width: "auto", textTransform: "capitalize" }}
          disabled={selectedRowKeys.length === 0}
        >
          <DeleteOutlinedIcon />
          {t("announcementTable.delete")}
        </Button>
      ),
      dataIndex: "delete",
      forceRender: true,
      render: (_value: string, record: AnnouncementGlobal) => (
        <span>
          <button
            className="delete-btn"
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteAnnouncement([record.announcementId]);
            }}
            aria-label="Delete Announcement"
          >
            <DeleteOutlinedIcon />
          </button>
        </span>
      ),
      className: "txt-pre-wrap",
      align: "center",
      style: {
        minWidth: 150,
      },
    },
  ];

  return (
    <>
      <DataTable
        rowKey="announcementId"
        columns={columns}
        dataSource={announcements}
        textEmptyData="noAnnouncementsFound"
        onRequestSort={handleSort}
        onCellClick={handleCellClick}
        didScrollTop={() => {
          initScrollToTop();
          resetIsContinuousLoading();
        }}
        loading={announcementsLoading}
        skeletonRows={11}
      />

      <DataPagination
        isHidden={!announcements?.length}
        className="mt-25 sp-p24"
        currentPage={pageInfo.page}
        limitPage={pageInfo.limit}
        totalPage={pageInfo.total}
        handleChangePage={handleChangePage}
      />
    </>
  );
};

export default AnnouncementsTable;
